import { useMemo, useState } from 'react';
import { Ghost } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  EXPIRING_SOON_DAYS,
  PIPELINE_STAGE_HINTS,
  PIPELINE_STAGE_VARIANTS,
  buildPipeline,
  daysUntilExpiry,
  getPipelineStage,
  type PipelineSignalId,
  type PipelineStage,
} from '@/lib/pipeline';
import type { Quote } from '@/types';
import PipelineSummary from './PipelineSummary';
import { QuoteSignatureStatus } from './QuoteSignatureStatus';
import { formatSignedDate, getQuoteSignedAt } from './signatureStatus';
import { euroRange, euroShort, ownerInitials, quoteValueLabel, quotesMaxValueCents, relativeDays } from './pipelineFormat';

/**
 * De Pipeline-tab: dezelfde offertes als het overzicht, maar gegroepeerd per
 * fase met bedrag en aantal per kolom. Een eigen tab en geen weergave van de
 * lijst, want hier vervangen de kolommen de statustabs en gaat het om het
 * totaalbeeld in plaats van om losse offertes bewerken.
 *
 * De kolommen volgen de opgeslagen status, met één afleiding: zodra er getekend
 * is maar nog niet door iedereen, staat een offerte in Ondertekening in plaats
 * van bij Bekeken of Geaccepteerd. Anders zou een deal die al bij de notaris
 * ligt er hetzelfde uitzien als eentje die net is opengeklikt.
 */

interface QuotePipelineBoardProps {
  /** Offertes na zoek- en filterselectie, zonder statustab-filter. */
  quotes: Quote[];
  onOpenQuote: (quote: Quote) => void;
}

/** Eén regel onder de bedrijfsnaam: nummer plus het laatste dat er gebeurd is. */
function cardMeta(quote: Quote, now: number) {
  const parts: string[] = [];
  if (quote.quoteNumber) parts.push(quote.quoteNumber);

  switch (getPipelineStage(quote)) {
    case 'concept':
      parts.push(`bijgewerkt ${relativeDays(quote.updatedAt, now)}`);
      break;
    case 'gepubliceerd':
      parts.push(
        (quote.viewCount ?? 0) === 0
          ? 'nog niet geopend'
          : `bijgewerkt ${relativeDays(quote.updatedAt, now)}`,
      );
      break;
    case 'bekeken':
      parts.push(`${quote.viewCount ?? 0}× bekeken`);
      break;
    /* Wie er getekend heeft staat in de chips eronder, hier alleen de tijdlijn. */
    case 'ondertekening':
      parts.push(`bijgewerkt ${relativeDays(quote.updatedAt, now)}`);
      break;
    case 'geaccepteerd': {
      const signedAt = getQuoteSignedAt(quote);
      parts.push(signedAt ? `getekend ${formatSignedDate(signedAt)}` : 'getekend');
      break;
    }
    default:
      break;
  }

  return parts.join(' · ');
}

/**
 * Hoogstens één badge per kaart: het dringendste signaal wint, anders blijft de
 * rij rommelig. De ondertekenstatus zit niet in deze rij — die heeft een eigen
 * chipregel, want daar horen twee partijen bij.
 */
function TrailingBadge({ quote, now }: { quote: Quote; now: number }) {
  if (quote.isGhost) {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-dashed text-muted-foreground"
        title="Aangemaakt en daarna nooit geopend of bewerkt"
      >
        <Ghost className="h-3 w-3 shrink-0" />
        Spook
      </Badge>
    );
  }

  const days = daysUntilExpiry(quote, now);
  if (days !== null && days >= 0 && days <= EXPIRING_SOON_DAYS) {
    return (
      <Badge variant="warning" title="Vervaldatum komt eraan">
        nog {days} d
      </Badge>
    );
  }

  if ((quote.outdatedProductRows ?? 0) > 0) {
    return (
      <Badge variant="secondary" title="Bevat regels op een oudere productversie">
        Oude versie
      </Badge>
    );
  }

  return null;
}

function PipelineCard({
  quote,
  now,
  onOpen,
}: {
  quote: Quote;
  now: number;
  onOpen: (quote: Quote) => void;
}) {
  const title = quote.companyName || quote.title;

  return (
    <button
      type="button"
      onClick={() => onOpen(quote)}
      className="w-full rounded border bg-card p-3 text-left shadow-sm transition-colors hover:border-muted-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
    >
      <div className="truncate text-sm font-semibold" title={title}>
        {title}
      </div>
      <div className="mt-1 truncate text-xs text-muted-foreground">{cardMeta(quote, now)}</div>
      <QuoteSignatureStatus quote={quote} compact className="mt-1.5" />
      <div className="mt-2 flex items-center gap-2">
        <span
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary text-[10px] font-semibold text-[#33475b]"
          title={quote.dealOwnerName || 'Geen dealeigenaar'}
        >
          {ownerInitials(quote)}
        </span>
        <span className="flex-1 truncate text-sm font-semibold tabular-nums">
          {quoteValueLabel(quote, euroShort)}
        </span>
        <TrailingBadge quote={quote} now={now} />
      </div>
    </button>
  );
}

function BoardColumn({
  stage,
  now,
  onOpenQuote,
}: {
  stage: PipelineStage;
  now: number;
  onOpenQuote: (quote: Quote) => void;
}) {
  return (
    <div className="flex min-w-[232px] flex-1 flex-col rounded bg-muted p-3">
      <div className="flex items-center justify-between gap-2 pb-2.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <Badge variant={PIPELINE_STAGE_VARIANTS[stage.id]} title={PIPELINE_STAGE_HINTS[stage.id]}>
            {stage.label}
          </Badge>
          <span className="text-xs text-muted-foreground">{stage.quotes.length}</span>
        </div>
        <span className="shrink-0 text-sm font-semibold tabular-nums">
          {euroRange(stage.valueCents, quotesMaxValueCents(stage.quotes), euroShort)}
        </span>
      </div>

      {stage.quotes.length === 0 ? (
        <p className="rounded border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">
          Geen offertes
        </p>
      ) : (
        /* Kolommen lopen ver uiteen — Concept is al snel tien keer zo lang als
           Getekend. Per kolom scrollen houdt het bord op één schermhoogte. */
        <div className="scrollbar-thin flex max-h-[560px] flex-col gap-2 overflow-y-auto pr-1">
          {stage.quotes.map((quote) => (
            <PipelineCard key={quote.id} quote={quote} now={now} onOpen={onOpenQuote} />
          ))}
        </div>
      )}
    </div>
  );
}

export function QuotePipelineBoard({ quotes, onOpenQuote }: QuotePipelineBoardProps) {
  const [activeSignal, setActiveSignal] = useState<PipelineSignalId | null>(null);

  /** Eén tijdstip per berekening: anders verspringen 'nog 7 d' en 'vandaag' tussen kaarten onderling. */
  const { now, data } = useMemo(() => {
    const timestamp = Date.now();
    return { now: timestamp, data: buildPipeline(quotes, timestamp) };
  }, [quotes]);

  const signal = data.signals.find((entry) => entry.id === activeSignal) ?? null;

  /** Bij een actief signaal blijven de kolommen staan, maar tonen ze alleen de gemarkeerde offertes. */
  const stages = useMemo(() => {
    if (!signal) return data.stages;
    const ids = new Set(signal.quoteIds);
    return data.stages.map((stage) => {
      const filtered = stage.quotes.filter((quote) => ids.has(quote.id));
      return {
        ...stage,
        quotes: filtered,
        valueCents: filtered.reduce((total, quote) => total + (quote.valueCents ?? 0), 0),
      };
    });
  }, [data.stages, signal]);

  return (
    <div className="space-y-4">
      <PipelineSummary data={data} />

      {data.signals.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="pr-1 text-xs font-semibold text-muted-foreground">Vraagt actie</span>
          {data.signals.map((entry) => {
            const active = entry.id === activeSignal;
            return (
              <Button
                key={entry.id}
                variant={active ? 'secondary' : 'outline'}
                size="sm"
                title={entry.hint}
                aria-pressed={active}
                className={cn('h-8 gap-2', entry.id === 'spook' && !active && 'border-dashed')}
                onClick={() => setActiveSignal(active ? null : entry.id)}
              >
                {entry.label}
                <Badge variant={entry.variant}>{entry.count}</Badge>
              </Button>
            );
          })}
          {signal && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs"
              onClick={() => setActiveSignal(null)}
            >
              Toon alles
            </Button>
          )}
        </div>
      )}

      <div className="flex items-start gap-3 overflow-x-auto pb-1">
        {stages.map((stage) => (
          <BoardColumn key={stage.id} stage={stage} now={now} onOpenQuote={onOpenQuote} />
        ))}
      </div>
    </div>
  );
}

export default QuotePipelineBoard;
